import * as React from 'react'
import { FlatList, View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import IncidentCard from '@components/incidentCard/IncidentCard'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'

import useReducer from '@store/useReducer'
import * as IncidentStore from '@store/incident/incident'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { isPrestaIncidentUnaffected } from '@utils/incidentUtils'
import { OPEN_INCIDENT_STATES } from '@utils/useSiteChangeHandler'

import api from '@api/api'
import logger from '@utils/logger'

const UnaffectedIncidentListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const unaffectedIncidents = incidents.filter(isPrestaIncidentUnaffected)
  const quoteWantedIncidents = incidents.filter((inc) => inc.status === 'EN_ATTENTE_DE_DEVIS')

  const tabs: { label: string; value: string }[] = [
    {
      label: i18n.t('screens.unaffectedIncidents.unaffected', { count: unaffectedIncidents.length }),
      value: 'unaffected',
    },
    {
      label: i18n.t('screens.unaffectedIncidents.quoteWanted', { count: quoteWantedIncidents.length }),
      value: 'quoteWanted',
    },
  ]

  const [tab, setTab] = React.useState(tabs[0])

  React.useEffect(() => {
    Promise.all([
      api.incident.search({ siteId: site.id, etatsIncident: OPEN_INCIDENT_STATES }),
      api.familles.all(site.id, true),
      api.localisation.all(site.id, true),
      api.societe.all(site.id),
    ])
      .then(([incidents, activities, places, providers]) => {
        IncidentStore.actions.setIncidents(incidents)
        FamilleStore.actions.setActivities(activities)
        LocationStore.actions.setPlaces(places)
        SocieteStore.actions.setServices(providers)
      })
      .catch(logger.error)
  }, [])

  const renderIncidentCard = ({ item }: { item: IncidentResponse }) => (
    <IncidentContainer
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}>
      <IncidentCard incident={item} />
    </IncidentContainer>
  )

  return (
    <Root header={isPhone ? i18n.t('screens.unaffectedIncidents.title') : undefined} onLeft={navigation.goBack}>
      <View>
        {!isPhone && <Header title={i18n.t('screens.unaffectedIncidents.title')} />}

        {isPhone && (
          <SegmentedContainer>
            <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
          </SegmentedContainer>
        )}

        <Subtitle>
          {!isPhone || tab.value === 'unaffected'
            ? i18n.t('screens.unaffectedIncidents.total', { count: unaffectedIncidents.length })
            : i18n.t('screens.unaffectedIncidents.quoteTotal', { count: quoteWantedIncidents.length })}
        </Subtitle>
        <FlatList
          data={!isPhone || tab.value === 'unaffected' ? unaffectedIncidents : quoteWantedIncidents}
          renderItem={renderIncidentCard}
          contentContainerStyle={{
            flexWrap: 'wrap',
            flexDirection: isPhone ? 'column' : 'row',
            padding: isPhone ? 16 : 24,
            justifyContent: isPhone ? 'center' : 'flex-start',
          }}
          ListEmptyComponent={<MissingInfo>{i18n.t('screens.unaffectedIncidents.noIncident')}</MissingInfo>}
          showsVerticalScrollIndicator={false}
        />
        {!isPhone && (
          <>
            <Subtitle>
              {i18n.t('screens.unaffectedIncidents.quoteTotal', { count: quoteWantedIncidents.length })}
            </Subtitle>
            <FlatList
              data={quoteWantedIncidents}
              renderItem={renderIncidentCard}
              contentContainerStyle={{
                flexWrap: 'wrap',
                flexDirection: isPhone ? 'column' : 'row',
                padding: isPhone ? 16 : 24,
                justifyContent: isPhone ? 'center' : 'flex-start',
              }}
              ListEmptyComponent={<MissingInfo>{i18n.t('screens.unaffectedIncidents.noIncident')}</MissingInfo>}
              showsVerticalScrollIndicator={false}
            />
          </>
        )}
      </View>
    </Root>
  )
}

export default UnaffectedIncidentListScreen

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const IncidentContainer = styled(TouchableOpacity)`
  margin-bottom: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `padding: 4px 0px;` : `margin-right: 16px;`)};
`

const Subtitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? `16px 16px 0 16px` : `16px 24px 0px`)};
`
const SegmentedContainer = styled(View)`
  padding: 16px;
`
